import { api } from "./api";

export interface WorkflowRunBreakdown {
  succeeded: number;
  failed: number;
  running: number;
  cancelled: number;
}

export interface TopFailingWorkflow {
  workflow_id: string;
  workflow_name: string;
  failed_count: number;
}

export interface DashboardOverview {
  workflows_total: number;
  workflows_enabled: number;
  runs_24h: WorkflowRunBreakdown;
  webhook_endpoints: number;
  aggregate_buckets_active: number;
  top_failing_workflows_24h: TopFailingWorkflow[];
}

export interface HourBucket {
  hour: string;
  succeeded: number;
  failed: number;
  running: number;
  cancelled: number;
}

export interface RunsTimeseries {
  bucket_size: string;
  hours: number;
  buckets: HourBucket[];
}

export interface ActivityItem {
  execution_id: string;
  workflow_id: string;
  workflow_name: string;
  direction: string;
  trigger: "webhook" | "cron" | "manual" | "event" | "replay";
  status: "running" | "succeeded" | "failed" | "cancelled";
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  error_preview: string | null;
}

export interface LatencyRow {
  workflow_id: string;
  workflow_name: string;
  direction: string;
  run_count: number;
  avg_ms: number;
  p95_ms: number;
  max_ms: number;
}

export interface StaleBucket {
  bucket_id: string;
  workflow_id: string;
  workflow_name: string;
  node_id: string;
  correlation_key: string;
  count: number;
  count_target: number | null;
  age_minutes: number;
}

// --- Per-plane summary cards (mission control) -----------------------------
// Each plane has its own gated endpoint; the frontend only calls the ones the
// operator may see, and derives the cross-plane "attention" strip from these
// payloads' `attention` blocks (no extra round-trip).

export interface AppsSummary {
  apps_total: number;
  apps_enabled: number;
  portal_users_total: number;
  portal_users_active_24h: number;
  forms_total: number;
  data_writes_24h: number;
  attention: { pending_registrations: number };
}

export interface DataSummary {
  datasets_total: number;
  datasets_published: number;
  sources_total: number;
  knowledge_bases_total: number;
  kb_documents_ready: number;
  attention: { sources_down: number; kb_ingests_failed: number };
}

export interface EtlRunBreakdown {
  succeeded: number;
  failed: number;
  running: number;
  pending: number;
  cancelled: number;
}

export interface EtlHourBucket {
  hour: string;
  total: number;
  failed: number;
}

export interface EtlSummary {
  pipelines_total: number;
  pipelines_enabled: number;
  runs_24h: EtlRunBreakdown;
  rows_out_24h: number;
  buckets: EtlHourBucket[];
}

export interface AiSurfaceSpend {
  surface: string;
  cost_usd: number;
  turns: number;
}

export interface AiSummary {
  days: number;
  total_cost_usd: number;
  total_tokens: number;
  turns: number;
  assistant_threads_total: number;
  monthly_budget_usd: number;
  spend_month_usd: number;
  by_surface: AiSurfaceSpend[];
}

export interface OpsSummary {
  open_alerts_24h: number;
  dead_letters: number;
  notifications_24h: number;
  attention: { dead_letters: number; open_alerts: number };
}

export interface GatewaysSummary {
  total: number;
  online: number;
  offline: number;
  revoked: number;
  enrolling: number;
  attention: { down: number };
}

export const dashboardApi = {
  overview: () => api.get<DashboardOverview>("/dashboard/overview"),

  appsSummary: () => api.get<AppsSummary>("/dashboard/apps-summary"),

  dataSummary: () => api.get<DataSummary>("/dashboard/data-summary"),

  etlSummary: () => api.get<EtlSummary>("/dashboard/etl-summary"),

  aiSummary: (days = 30) => api.get<AiSummary>(`/dashboard/ai-summary?days=${days}`),

  opsSummary: () => api.get<OpsSummary>("/dashboard/ops-summary"),

  gatewaysSummary: () => api.get<GatewaysSummary>("/dashboard/gateways-summary"),

  runsTimeseries: (hours = 24) =>
    api.get<RunsTimeseries>(`/dashboard/runs-timeseries?hours=${hours}`),

  recentActivity: (limit = 20) =>
    api.get<ActivityItem[]>(`/dashboard/recent-activity?limit=${limit}`),

  latency: (hours = 24, limit = 10) =>
    api.get<LatencyRow[]>(`/dashboard/latency?hours=${hours}&limit=${limit}`),

  staleBuckets: (minutes = 60) =>
    api.get<StaleBucket[]>(`/dashboard/stale-buckets?minutes=${minutes}`),
};
